import React, { useEffect, useMemo, useRef, useState } from "react";
import { createUniver, LocaleType, mergeLocales } from "@univerjs/presets";
import { UniverSheetsCorePreset } from "@univerjs/preset-sheets-core";
import { UniverSheetsNotePreset } from "@univerjs/preset-sheets-note";
import { UniverSheetsThreadCommentPreset } from "@univerjs/preset-sheets-thread-comment";
import SheetsCorePtBR from "@univerjs/preset-sheets-core/locales/pt-BR";
import UniverPresetSheetsNotePtBR from "@univerjs/preset-sheets-note/locales/pt-BR";
import UniverPresetSheetsThreadCommentPtBR from "@univerjs/preset-sheets-thread-comment/locales/pt-BR";
import { Box } from "@mui/material";
import "@univerjs/preset-sheets-core/lib/index.css";
import "@univerjs/preset-sheets-note/lib/index.css";
import { spreadsheetStyles } from "../../../../utils/spreadsheet/styles";
import { composeTables } from "../../../../utils/spreadsheet/tableBuilder";
import { cellRefToRowCol } from "../../../../utils/spreadsheet/utils";

const ROW_PADDING = 50;
const MIN_COLUMNS = 30;

export const useBeforeRender = (callback, deps) => {
  const [isRun, setIsRun] = useState(false);
  if (!isRun) {
    callback();
    setIsRun(true);
  }
  useEffect(() => () => setIsRun(false), [deps]);
};

const UniverSpreadsheet = ({
  tables = [],
  sheetsConfig = null, // NOVO: array de { name, tables, startPosition? }
  initialSnapshot = null,
  layout = "vertical",
  startPosition = { row: 0, col: 0 },
  onReady,
  height = 600,
  locale = LocaleType.PT_BR,
}) => {
  const containerRef = useRef(null);
  const univerAPIRef = useRef(null);
  const isInitializedRef = useRef(false);
  const prevWorkbookDataRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);

  useBeforeRender(() => {
    window.addEventListener("error", (e) => {
      if (e) {
        const resizeObserverErrDiv = document.getElementById(
          "webpack-dev-server-client-overlay-div",
        );
        const resizeObserverErr = document.getElementById(
          "webpack-dev-server-client-overlay",
        );
        if (resizeObserverErr)
          resizeObserverErr.className = "hide-resize-observer";
        if (resizeObserverErrDiv)
          resizeObserverErrDiv.className = "hide-resize-observer";
      }
    });
  }, []);

  const deepEqual = (obj1, obj2) => {
    return JSON.stringify(obj1) === JSON.stringify(obj2);
  };

  const resolvePosition = (position) => {
    if (!position) return null;
    if (typeof position === "string") return cellRefToRowCol(position);
    return position;
  };

  // Função para construir os dados de uma única sheet a partir de tabelas
  const buildSheetData = (tablesConfig, startPos = { row: 0, col: 0 }) => {
    if (!tablesConfig || tablesConfig.length === 0) return null;
    const configs = [];
    let autoRow = startPos.row;
    const autoCol = startPos.col;

    tablesConfig.forEach((table) => {
      const { definition, data, position } = table;
      if (!definition || !data) return;
      const resolved = resolvePosition(position);
      const startRow = resolved ? resolved.row : autoRow;
      const startCol = resolved ? resolved.col : autoCol;
      configs.push({ definition, data, startRow, startCol });
      if (!resolved) {
        autoRow = startRow + data.length + 5;
      }
    });

    if (configs.length === 0) return null;

    const { cellData, columnData, totalRows, totalColumns, mergedCells } =
      composeTables(configs);

    return {
      cellData,
      columnData,
      rowCount: Math.max(totalRows + ROW_PADDING, ROW_PADDING),
      columnCount: Math.max(totalColumns, MIN_COLUMNS),
      mergeData: mergedCells.map((merge) => ({
        startRow: merge.startRow,
        startColumn: merge.startCol,
        endRow: merge.endRow,
        endColumn: merge.endCol,
      })),
    };
  };

  // Construir o workbookData (pode ter múltiplas sheets ou uma única)
  const workbookData = useMemo(() => {
    if (initialSnapshot) return initialSnapshot;

    // Se temos sheetsConfig, construir múltiplas sheets
    if (sheetsConfig && sheetsConfig.length > 0) {
      const sheets = {};
      const sheetOrder = [];

      sheetsConfig.forEach((sheetConfig, index) => {
        const {
          name,
          tables: sheetTables,
          startPosition: sheetStartPos = { row: 0, col: 0 },
        } = sheetConfig;
        if (!sheetTables || sheetTables.length === 0) return;

        const sheetData = buildSheetData(sheetTables, sheetStartPos);
        if (!sheetData) return;

        const sheetId = `sheet-${index + 1}`;
        sheets[sheetId] = {
          id: sheetId,
          name: name || `Planilha ${index + 1}`,
          defaultColumnWidth: 120,
          ...sheetData,
        };
        sheetOrder.push(sheetId);
      });

      if (Object.keys(sheets).length === 0) return null;

      return {
        id: `spreadsheet-${Date.now()}`,
        name: "Planilha",
        appVersion: "0.10.2",
        locale,
        styles: spreadsheetStyles,
        sheetOrder,
        sheets,
      };
    }

    // Comportamento antigo: uma única sheet com tables
    if (!tables || tables.length === 0) return null;

    const sheetData = buildSheetData(tables, startPosition);
    if (!sheetData) return null;

    return {
      id: `spreadsheet-${Date.now()}`,
      name: "Planilha",
      appVersion: "0.10.2",
      locale,
      styles: spreadsheetStyles,
      sheetOrder: ["sheet-1"],
      sheets: {
        "sheet-1": {
          id: "sheet-1",
          name: "Planilha",
          defaultColumnWidth: 120,
          ...sheetData,
        },
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tables, sheetsConfig, initialSnapshot, startPosition, locale]);

  // Inicialização (apenas uma vez)
  useEffect(() => {
    if (!containerRef.current || isInitializedRef.current) return;
    const dataToLoad = initialSnapshot || workbookData;
    if (!dataToLoad) return;

    isInitializedRef.current = true;

    const locales = {
      [LocaleType.PT_BR]: mergeLocales(
        SheetsCorePtBR,
        UniverPresetSheetsNotePtBR,
        UniverPresetSheetsThreadCommentPtBR,
      ),
    };

    const { univerAPI } = createUniver({
      locale: locale || LocaleType.PT_BR,
      locales,
      presets: [
        UniverSheetsCorePreset({
          container: containerRef.current,
        }),
        UniverSheetsNotePreset({
          container: containerRef.current,
        }),
        UniverSheetsThreadCommentPreset({
          container: containerRef.current,
        }),
      ],
    });

    univerAPI.createWorkbook(dataToLoad);
    univerAPIRef.current = univerAPI;
    prevWorkbookDataRef.current = dataToLoad;
    setIsLoading(false);

    if (onReady) {
      onReady(univerAPI);
    }

    return () => {
      if (univerAPIRef.current) {
        univerAPIRef.current.dispose();
        univerAPIRef.current = null;
      }
      isInitializedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Atualização: apenas guarda o novo dado, mas não recria (pode ser melhorado depois)
  useEffect(() => {
    if (!isInitializedRef.current || !univerAPIRef.current || !workbookData)
      return;
    if (deepEqual(prevWorkbookDataRef.current, workbookData)) return;
    prevWorkbookDataRef.current = workbookData;
    // Aqui você poderia usar a Facade API para recarregar, mas como a inicialização é única,
    // deixamos assim por enquanto.
  }, [workbookData]);

  return (
    <Box style={{ width: "100%", position: "relative" }}>
      {isLoading && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            zIndex: 10,
          }}>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      )}
      <Box
        ref={containerRef}
        style={{
          width: "100%",
          height: `${height}px`,
          border: "1px solid #e0e0e0",
        }}
      />
    </Box>
  );
};

export default UniverSpreadsheet;
